import React, { useEffect, useMemo, useRef, useState } from 'react';
import { View, Text, TouchableOpacity, StyleSheet, ActivityIndicator, ScrollView } from 'react-native';
import { FontAwesome5 } from '@expo/vector-icons';

import { getBrandContentById, getStoreDetail, updateBrandContent } from '../../../../api/Settings';
import { msgStr } from '../../../../common/constants/Message';
import { useAlertModal, useConfirmModal } from '../../../../common/hooks';
import ContentField, { Setting } from '../../storedetails/brandContent/ContentField';
import { useCmsBridge } from './useCmsBridge';

interface Props {
  brandId: number;
  brandName: string;
  onClose: () => void;
}

// Storefront routes an operator can jump to from the inspector.
const ROUTES: { label: string; path: string }[] = [
  { label: 'Home', path: '/' },
  { label: 'Rental', path: '/rental' },
  { label: 'Reservation (legacy)', path: '/reservation' },
  { label: 'Quick Order', path: '/qr' },
  { label: 'Login', path: '/login' },
  { label: 'Thank You', path: '/rental/thankyou' },
];

// Operational settings that are content blocks but must not be edited inline.
const LOCKED_KEYS = new Set(['delivery_times', 'drive_off_times']);

const isLocalHost = (host: string) => /^(localhost|127\.0\.0\.1)(:\d+)?$/i.test(host);

const buildEditUrl = (host: string, path: string) => {
  const clean = host.replace(/^https?:\/\//i, '').replace(/\/+$/, '');
  const proto = isLocalHost(clean) ? 'http' : 'https';
  return `${proto}://${clean}${path}?cms_edit=1`;
};

/**
 * CMS "Edit Storefront" mode: the brand's real storefront in an iframe with
 * ?cms_edit=1, next to an inspector. Clicking an outlined region in the store
 * selects its content key; edits are pushed to the frame for live preview and
 * saved through the same updatebrandcontent endpoint the form editor uses.
 * The storefront never receives admin credentials.
 */
const StoreEditMode = ({ brandId, brandName, onClose }: Props) => {
  const { showAlert } = useAlertModal() as unknown as { showAlert: (t: string, m: string) => void };
  const { showConfirm } = useConfirmModal() as unknown as { showConfirm: (m: string, cb: () => void) => void };

  const [storeHost, setStoreHost] = useState<string>('');
  const [hostInput, setHostInput] = useState<string>('');
  const [startPath, setStartPath] = useState<string>('/');
  const [fields, setFields] = useState<Setting[]>([]);
  const [saved, setSaved] = useState<Record<string, string>>({});
  const [draft, setDraft] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [highlight, setHighlight] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const [frameNonce, setFrameNonce] = useState(0);

  const iframeSrc = useMemo(() => (storeHost ? buildEditUrl(storeHost, startPath) : ''), [storeHost, startPath, frameNonce]);
  const bridge = useCmsBridge(iframeSrc, brandId);
  const applyTimer = useRef<number | null>(null);

  // Load store URL + current content values for this brand.
  useEffect(() => {
    let alive = true;
    setLoading(true);
    Promise.all([
      new Promise<void>(resolve => getStoreDetail(brandId, (json: any, status: number) => {
        if (alive && status === 200 && json?.store_url) { setStoreHost(json.store_url); setHostInput(json.store_url); }
        resolve();
      })),
      new Promise<void>(resolve => getBrandContentById(String(brandId), (json: any, status: number) => {
        if (alive && status === 200 && Array.isArray(json)) {
          setFields(json);
          setSaved(Object.fromEntries(json.map((f: Setting) => [f.key_name, f.value || ''])));
        }
        resolve();
      })),
    ]).finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [brandId]);

  const dirtyKeys = useMemo(() => Object.keys(draft).filter(k => draft[k] !== (saved[k] ?? '')), [draft, saved]);
  const selectedField: Setting | null = useMemo(() => {
    const key = bridge.selection?.key;
    return key ? fields.find(f => f.key_name === key) || null : null;
  }, [bridge.selection, fields]);

  const currentValue = (key: string) => (key in draft ? draft[key] : saved[key] ?? '');

  // Edit → local draft + debounced live preview in the frame.
  const onEdit = (key: string, value: string) => {
    setDraft(prev => ({ ...prev, [key]: value }));
    if (applyTimer.current) window.clearTimeout(applyTimer.current);
    applyTimer.current = window.setTimeout(() => bridge.apply(key, value), 150);
  };

  const save = () => {
    if (dirtyKeys.length === 0) return;
    setSaving(true);
    const payload = { brand_id: brandId, fields: dirtyKeys.map(k => ({ key_name: k, value: draft[k] })) };
    updateBrandContent(payload, (json: any, status: number) => {
      setSaving(false);
      if (status === 200 || status === 201) {
        setSaved(prev => ({ ...prev, ...Object.fromEntries(dirtyKeys.map(k => [k, draft[k]])) }));
        setDraft({});
        showAlert('success', `Saved ${dirtyKeys.length} change(s) to ${brandName}`);
      } else {
        showAlert('error', json?.error || msgStr('unknownError'));
      }
    });
  };

  const discard = () => {
    if (dirtyKeys.length === 0) return;
    showConfirm(`Discard ${dirtyKeys.length} unsaved change(s)?`, () => {
      setDraft({});
      bridge.clearDraft();
    });
  };

  const close = () => {
    if (dirtyKeys.length > 0) showConfirm('You have unsaved changes. Close and discard them?', onClose);
    else onClose();
  };

  const toggleHighlight = () => { setHighlight(h => !h); bridge.highlightAll(!highlight); };

  const applyHost = () => {
    const h = hostInput.trim();
    if (!h) return;
    setStoreHost(h);
    setDraft({});
    setFrameNonce(n => n + 1);
  };

  const renderEditor = (field: Setting) => {
    const value = currentValue(field.key_name);
    if (LOCKED_KEYS.has(field.key_name)) {
      return (
        <View style={styles.lockedBox}>
          <FontAwesome5 name="lock" size={12} color="#92400e" />
          <Text style={styles.lockedText}>
            {field.label} is an operational setting. Edit it in CMS Settings → Brand Content.
          </Text>
        </View>
      );
    }
    if (field.field_type === 'color') {
      return (
        <View style={styles.colorRow}>
          <input
            type="color"
            value={/^#[0-9a-f]{6}$/i.test(value) ? value : '#000000'}
            onChange={(e) => onEdit(field.key_name, e.target.value)}
            style={{ width: 44, height: 34, border: '1px solid #ccc', borderRadius: 4, padding: 0, background: 'none' }}
          />
          <input
            type="text"
            value={value}
            placeholder="#RRGGBB"
            onChange={(e) => onEdit(field.key_name, e.target.value)}
            style={{ flex: 1, height: 34, border: '1px solid #ccc', borderRadius: 4, padding: '0 8px', fontFamily: 'monospace' }}
          />
        </View>
      );
    }
    return (
      <ContentField
        key={`${field.key_name}:${brandId}`}
        setting={{ ...field, value }}
        brandId={brandId}
        onValueChange={(_id, v) => onEdit(field.key_name, v)}
      />
    );
  };

  const onScreenKeys = new Set(bridge.keysOnScreen.map(k => k.key));
  // Nothing detected on this screen (e.g. a landing/login page) → fall back to the full list so
  // the operator can still edit any field with live preview.
  const listAll = showAll || onScreenKeys.size === 0;
  const listedFields = listAll ? fields : fields.filter(f => onScreenKeys.has(f.key_name) || f.field_type === 'color');

  return (
    <View style={styles.root}>
      {/* ── Top bar ── */}
      <View style={styles.topbar}>
        <TouchableOpacity onPress={close} style={styles.topBtn}>
          <FontAwesome5 name="arrow-left" size={13} color="#fff" />
          <Text style={styles.topBtnText}>Back to CMS</Text>
        </TouchableOpacity>
        <Text style={styles.topTitle}>Editing storefront · {brandName}</Text>
        <View style={styles.status}>
          <View style={[styles.dot, { backgroundColor: bridge.connected ? '#22c55e' : '#f59e0b' }]} />
          <Text style={styles.statusText}>{bridge.connected ? `connected · ${bridge.route}` : 'waiting for storefront…'}</Text>
        </View>
        <View style={{ flex: 1 }} />
        <TouchableOpacity onPress={toggleHighlight} style={[styles.topBtn, highlight && styles.topBtnActive]}>
          <FontAwesome5 name="border-all" size={12} color="#fff" />
          <Text style={styles.topBtnText}>{highlight ? 'Hide regions' : 'Show all regions'}</Text>
        </TouchableOpacity>
        <TouchableOpacity onPress={discard} disabled={dirtyKeys.length === 0} style={[styles.topBtn, dirtyKeys.length === 0 && styles.topBtnDisabled]}>
          <Text style={styles.topBtnText}>Discard</Text>
        </TouchableOpacity>
        <TouchableOpacity onPress={save} disabled={dirtyKeys.length === 0 || saving} style={[styles.topBtn, styles.saveBtn, (dirtyKeys.length === 0 || saving) && styles.topBtnDisabled]}>
          {saving ? <ActivityIndicator size="small" color="#fff" /> : <FontAwesome5 name="save" size={12} color="#fff" />}
          <Text style={styles.topBtnText}>Save{dirtyKeys.length ? ` (${dirtyKeys.length})` : ''}</Text>
        </TouchableOpacity>
      </View>

      <View style={styles.body}>
        {/* ── Storefront frame ── */}
        <View style={styles.frameCol}>
          <View style={styles.frameBar}>
            <Text style={styles.frameBarLabel}>Store:</Text>
            <input
              value={hostInput}
              onChange={(e) => setHostInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') applyHost(); }}
              placeholder="store.hosted.bodhisys.io or localhost:3001"
              style={{ flex: 1, height: 30, border: '1px solid #ccc', borderRadius: 4, padding: '0 8px', fontSize: 13 }}
            />
            <TouchableOpacity onPress={applyHost} style={styles.smallBtn}><Text style={styles.smallBtnText}>Load</Text></TouchableOpacity>
            <View style={styles.routeChips}>
              {ROUTES.map(r => (
                <TouchableOpacity
                  key={r.path}
                  style={[styles.chip, bridge.route === r.path && styles.chipActive]}
                  onPress={() => { setStartPath(r.path); if (bridge.connected) bridge.navigate(r.path); }}
                >
                  <Text style={[styles.chipText, bridge.route === r.path && styles.chipTextActive]}>{r.label}</Text>
                </TouchableOpacity>
              ))}
            </View>
          </View>
          {loading ? (
            <ActivityIndicator size="large" style={{ marginTop: 60 }} />
          ) : !iframeSrc ? (
            <Text style={styles.hint}>This brand has no store URL. Enter one above to load its storefront.</Text>
          ) : (
            <iframe
              key={iframeSrc}
              ref={bridge.iframeRef as any}
              src={iframeSrc}
              title="Storefront edit mode"
              onLoad={bridge.onIframeLoad}
              style={{ flex: 1, width: '100%', border: '1px solid #ddd', borderRadius: 6, background: '#fff' }}
            />
          )}
        </View>

        {/* ── Inspector ── */}
        <ScrollView style={styles.inspector} contentContainerStyle={{ padding: 14 }}>
          <Text style={styles.inspectorTitle}>Selected element</Text>
          {selectedField ? (
            <View style={styles.card}>
              <View style={styles.cardHead}>
                <Text style={styles.cardLabel}>{selectedField.label}</Text>
                <Text style={styles.cardKey}>{selectedField.key_name} · {selectedField.field_type}</Text>
              </View>
              {renderEditor(selectedField)}
              {dirtyKeys.includes(selectedField.key_name) && (
                <TouchableOpacity onPress={() => { setDraft(d => { const n = { ...d }; delete n[selectedField.key_name]; return n; }); bridge.clearDraft([selectedField.key_name]); }}>
                  <Text style={styles.revertLink}>Revert this field</Text>
                </TouchableOpacity>
              )}
            </View>
          ) : (
            <Text style={styles.hint}>
              {bridge.connected
                ? 'Click an outlined region in the store to edit it, or pick a field below.'
                : 'Waiting for the storefront to connect. If it never does, check that the store URL is right and that this admin origin is allowed to frame it.'}
            </Text>
          )}

          <View style={styles.listHead}>
            <Text style={styles.inspectorTitle}>{listAll ? 'All fields' : 'Fields on this screen'}</Text>
            {onScreenKeys.size > 0 && (
              <TouchableOpacity onPress={() => setShowAll(s => !s)}>
                <Text style={styles.link}>{showAll ? 'show on-screen only' : 'show all'}</Text>
              </TouchableOpacity>
            )}
          </View>
          {onScreenKeys.size === 0 && bridge.connected && (
            <Text style={styles.hint}>No clickable regions on this screen — navigate the store (or use the route chips) to reach the schedule / checkout screens, or edit any field below.</Text>
          )}
          {listedFields.map(f => {
            const dirty = dirtyKeys.includes(f.key_name);
            const active = bridge.selection?.key === f.key_name;
            return (
              <TouchableOpacity
                key={f.key_name}
                style={[styles.fieldRow, active && styles.fieldRowActive]}
                onPress={() => bridge.select({ key: f.key_name, fieldType: f.field_type as any, value: currentValue(f.key_name) })}
              >
                <View style={{ flex: 1 }}>
                  <Text style={styles.fieldLabel}>{f.label}{dirty ? ' •' : ''}</Text>
                  <Text style={styles.fieldPreview} numberOfLines={1}>
                    {f.field_type === 'image' ? (currentValue(f.key_name) || '(no image)') : (currentValue(f.key_name).replace(/<[^>]+>/g, '') || '(empty)')}
                  </Text>
                </View>
                {LOCKED_KEYS.has(f.key_name) && <FontAwesome5 name="lock" size={11} color="#999" />}
                {f.field_type === 'color' && !!currentValue(f.key_name) && (
                  <View style={[styles.swatch, { backgroundColor: currentValue(f.key_name) }]} />
                )}
              </TouchableOpacity>
            );
          })}
        </ScrollView>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  root: { flex: 1, backgroundColor: '#f3f4f6' },
  topbar: { flexDirection: 'row', alignItems: 'center', gap: 8, paddingHorizontal: 12, paddingVertical: 8, backgroundColor: '#1f2937' },
  topBtn: { flexDirection: 'row', alignItems: 'center', gap: 6, backgroundColor: '#374151', paddingVertical: 7, paddingHorizontal: 12, borderRadius: 5 },
  topBtnActive: { backgroundColor: '#2e96e1' },
  topBtnDisabled: { opacity: 0.45 },
  saveBtn: { backgroundColor: '#16a34a' },
  topBtnText: { color: '#fff', fontWeight: '600', fontSize: 13 },
  topTitle: { color: '#fff', fontWeight: '700', fontSize: 14, marginLeft: 8 },
  status: { flexDirection: 'row', alignItems: 'center', gap: 6, marginLeft: 12 },
  dot: { width: 8, height: 8, borderRadius: 4 },
  statusText: { color: '#d1d5db', fontSize: 12 },
  body: { flex: 1, flexDirection: 'row' },
  frameCol: { flex: 7, padding: 10, gap: 8 },
  frameBar: { flexDirection: 'row', alignItems: 'center', gap: 8, flexWrap: 'wrap' },
  frameBarLabel: { fontSize: 13, fontWeight: '600', color: '#374151' },
  smallBtn: { backgroundColor: '#2e96e1', paddingVertical: 6, paddingHorizontal: 12, borderRadius: 4 },
  smallBtnText: { color: '#fff', fontSize: 12, fontWeight: '600' },
  routeChips: { flexDirection: 'row', gap: 6, marginLeft: 8, flexWrap: 'wrap' },
  chip: { borderWidth: 1, borderColor: '#2e96e1', borderRadius: 12, paddingVertical: 4, paddingHorizontal: 10, backgroundColor: '#fff' },
  chipActive: { backgroundColor: '#2e96e1' },
  chipText: { fontSize: 12, color: '#2e96e1', fontWeight: '600' },
  chipTextActive: { color: '#fff' },
  inspector: { flex: 3, minWidth: 320, maxWidth: 480, backgroundColor: '#fff', borderLeftWidth: 1, borderLeftColor: '#e5e7eb' },
  inspectorTitle: { fontSize: 12, fontWeight: '700', color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8 },
  card: { borderWidth: 1, borderColor: '#f59e0b', borderRadius: 6, padding: 12, marginBottom: 18, backgroundColor: '#fffbeb' },
  cardHead: { marginBottom: 8 },
  cardLabel: { fontSize: 15, fontWeight: '700', color: '#111827' },
  cardKey: { fontSize: 11, color: '#6b7280', fontFamily: 'monospace' },
  revertLink: { color: '#b45309', fontSize: 12, marginTop: 8, textDecorationLine: 'underline' },
  listHead: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginTop: 6 },
  link: { color: '#2e96e1', fontSize: 12, textDecorationLine: 'underline', marginBottom: 8 },
  fieldRow: { flexDirection: 'row', alignItems: 'center', gap: 8, paddingVertical: 8, paddingHorizontal: 10, borderRadius: 5, borderWidth: 1, borderColor: '#e5e7eb', marginBottom: 6 },
  fieldRowActive: { borderColor: '#f59e0b', backgroundColor: '#fffbeb' },
  fieldLabel: { fontSize: 13, fontWeight: '600', color: '#111827' },
  fieldPreview: { fontSize: 11, color: '#6b7280', marginTop: 2 },
  swatch: { width: 18, height: 18, borderRadius: 3, borderWidth: 1, borderColor: '#ccc' },
  lockedBox: { flexDirection: 'row', gap: 8, alignItems: 'center', backgroundColor: '#fef3c7', padding: 10, borderRadius: 5 },
  lockedText: { color: '#92400e', fontSize: 12, flex: 1 },
  colorRow: { flexDirection: 'row', gap: 8, alignItems: 'center' },
  hint: { fontSize: 13, color: '#6b7280', fontStyle: 'italic', marginBottom: 16, lineHeight: 18 },
});

export default StoreEditMode;
